import { cacheSettings } from 'src/config/setting';
import { isNullOrUnDef } from './is';
import { AesEncryption } from './encryption';
export interface CacheConfig {
  key: string;
  storage: Storage;
  value?: any;
  iv?: string;
  hasEncrypt?: boolean;
  timeout?: null | number;
}
export interface StorageLike {
  set: (key: string, value: any, expire?: number | null) => void;
  get: <T = any>(key: string, def?: T | null) => T | null;
  remove: (key: string) => void;
  clear: () => void;
}

export const createStorage = ({
  storage = sessionStorage,
  key = cacheSettings.cacheCipher.key,
  iv = cacheSettings.cacheCipher.iv,
  timeout = cacheSettings.cacheTimeOut,
  hasEncrypt = false,
}: Partial<CacheConfig> = {}): StorageLike => {
  if (hasEncrypt && [key.length, iv.length].some((item) => item !== 16)) {
    throw new Error('密钥或iv必须为16位!');
  }

  const encryption = new AesEncryption({ key, iv });
  const storageInstance = storage;

  const formatKey = (cacheKey: string) => `${cacheKey}`.toUpperCase();

  const set = (
    cacheKey: string,
    value: any,
    expire: number | null = timeout
  ) => {
    const stringData = JSON.stringify({
      value,
      time: Date.now(),
      expire: !isNullOrUnDef(expire)
        ? new Date().getTime() + expire * 1000
        : null,
    });
    const stringifyValue = hasEncrypt
      ? encryption.encryptByAES(stringData)
      : stringData;
    storageInstance.setItem(formatKey(cacheKey), stringifyValue);
  };

  const get = <T = any>(cacheKey: string, def: T | null = null): T | null => {
    const val = storageInstance.getItem(formatKey(cacheKey));
    if (!val) return def;

    try {
      const decVal = hasEncrypt ? encryption.decryptByAES(val) : val;
      const data = JSON.parse(decVal) as { value: T; expire: number | null };
      const { value, expire } = data;
      if (isNullOrUnDef(expire) || expire >= new Date().getTime()) {
        return value;
      }
      remove(cacheKey);
    } catch (e) {
      return def;
    }

    return def;
  };

  const remove = (cacheKey: string) => {
    storageInstance.removeItem(formatKey(cacheKey));
  };

  const clear = () => {
    storageInstance.clear();
  };

  return {
    set,
    get,
    remove,
    clear,
  };
};
